// Path-restricted public marketplace API module.
// Uses a token-less client (createPublicApiClient) — no Authorization header is ever attached.
// Only the 4 public marketplace routes are reachable; no arbitrary-path method is exposed.
// See spec EAINT-663 — public marketplace access (Phase 1).

import { createPublicApiClient, type ApiClient } from '@center-technologies/shared-api-clients';

// ============================================================
// Response types
// Mirror the backend allowlist shapes from publicDetailHelpers + discover route.
// ============================================================

export interface PublicDiscoverListing {
  system_name: string;
  name: unknown;
  description: unknown;
  glyph: string | null;
  glyph_kind: string;
  tags: string[];
  publisher: { slug: string; name: string } | null;
}

export interface PublicDiscoverResponse {
  data: PublicDiscoverListing[];
  total: number;
}

export interface PublicDiscoverParams {
  useCaseTags?: string[];
  limit?: number;
  offset?: number;
}

export interface PublicListedTemplate {
  system_name: string;
  type: string;
  name: string | null;
  description: string | null;
  glyph: string | null;
  tags: string[];
}

export interface PublicChannelDetail {
  system_name: string;
  name: unknown;
  description: unknown;
  glyph: string | null;
  glyph_kind: string;
  hue: number | null;
  tags: string[];
  publisher: { slug: string; name: string | null } | null;
  listed_templates: PublicListedTemplate[];
}

export interface PublicPublisherChannel {
  system_name: string;
  name: unknown;
  glyph: string | null;
}

export interface PublicPublisherDetail {
  slug: string;
  name: string;
  bio: string | null;
  glyph: string | null;
  hue: number;
  is_official: boolean;
  website: string | null;
  socials: Record<string, string> | null;
  channels: PublicPublisherChannel[];
}

export interface PublicTemplateDetail {
  system_name: string;
  type: string;
  name: string | null;
  description: string | null;
  glyph: string | null;
  tags: string[];
  publisher: { slug: string; name: string | null } | null;
}

// ============================================================
// Internal client
// Created fresh per call — env var is resolved at call time and test isolation is clean.
// createPublicApiClient is cheap (no session, no token setup), so per-call creation is acceptable.
// ============================================================

function getClient(): ApiClient {
  return createPublicApiClient(
    process.env.REACT_APP_SCHEMA_ENDPOINT || 'http://localhost:3002'
  );
}

// ============================================================
// Public marketplace API
// Exactly 4 methods; each targets a fixed path — no caller-supplied path.
// ============================================================

export const publicMarketplaceApi = {
  discover: (params?: PublicDiscoverParams): Promise<PublicDiscoverResponse> => {
    return getClient().get<PublicDiscoverResponse>('/marketplace/discover', { params });
  },

  getChannel: (systemName: string): Promise<PublicChannelDetail> => {
    return getClient().get<PublicChannelDetail>(
      `/marketplace/channels/${encodeURIComponent(systemName)}`
    );
  },

  getPublisher: (slug: string): Promise<PublicPublisherDetail> => {
    return getClient().get<PublicPublisherDetail>(
      `/marketplace/publishers/${encodeURIComponent(slug)}`
    );
  },

  getTemplate: (systemName: string): Promise<PublicTemplateDetail> => {
    return getClient().get<PublicTemplateDetail>(
      `/marketplace/templates/${encodeURIComponent(systemName)}`
    );
  },
};
